import {
  getMigrationStatus,
  importLegacySite,
  type LegacyMigrationKind,
  type MigrationStatusItem,
} from "@/api/cms-puck/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button.tsx";
import { useToast } from "@/hooks/use-toast";
import { CmsAdminShell } from "@/pages/cms/advanced/AdminShell";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check, FileText, LayoutTemplate } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";

const STATUS_KEY = ["cms-puck", "migration-status"] as const;
const PAGES_KEY = ["cms-puck", "pages"] as const;
const HOME_KEY = ["cms-puck", "home"] as const;

/** Modèle détecté pour chaque type de page de l'ancien CMS (affichage). */
const DETECTED_TEMPLATE: Partial<Record<LegacyMigrationKind, string>> = {
  homepage: "Page d'accueil",
  about: "À propos",
  contact: "Contact",
  artist: "Fiche Artiste",
  news: "Actualités",
  expertise: "Expertise",
  selection: "Sélection d'œuvres",
  eshop: "Boutique",
  catalogue: "Catalogue raisonné",
};

export default function CMSAdvancedImportAncienSite() {
  const cmsNew = useCmsNew();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const statusQuery = useQuery({ queryKey: STATUS_KEY, queryFn: getMigrationStatus });

  const items: MigrationStatusItem[] = useMemo(
    () => (statusQuery.data?.items ?? []).filter((it) => it.legacyAvailable),
    [statusQuery.data],
  );

  // Ré-import = IGNORER : les pages déjà importées ne sont pas ré-importables
  // (le back les saute de toute façon). On ne coche par défaut que les autres.
  const importable = useMemo(
    () => items.filter((it) => !it.puckPageId),
    [items],
  );

  const [selected, setSelected] = useState<Set<LegacyMigrationKind>>(new Set());
  useEffect(() => {
    setSelected(new Set(importable.map((it) => it.kind)));
  }, [importable]);

  const toggle = (kind: LegacyMigrationKind) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });

  const selectedCount = selected.size;

  const importMutation = useMutation({
    mutationFn: () =>
      importLegacySite(
        items
          .filter((it) => selected.has(it.kind))
          .map((it) => ({ kind: it.kind })),
      ),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: PAGES_KEY });
      queryClient.invalidateQueries({ queryKey: HOME_KEY });
      queryClient.invalidateQueries({ queryKey: STATUS_KEY });
      toast({
        title: cmsNew("cms.new.import.success", "Import terminé"),
        description: cmsNew(
          "cms.new.import.successDesc",
          `${res.count} page(s) importée(s) en brouillon.`,
        ),
        variant: "success",
        duration: 2500,
      });
      navigate("/cms/advanced");
    },
    onError: () => {
      toast({
        title: cmsNew("cms.new.import.failed", "Échec de l'import"),
        variant: "error",
      });
    },
  });

  return (
    <CmsAdminShell
      title={cmsNew("cms.new.import.title", "Importer mon ancien site")}
      subtitle={cmsNew(
        "cms.new.import.subtitle",
        "Reprends les pages de l'ancien site en une fois. Chaque page est recréée en brouillon via le modèle correspondant — rien n'est publié automatiquement, l'ancien site reste intact.",
      )}
      actions={
        <Button variant="ghost" size="sm" onClick={() => navigate("/cms/advanced")}>
          <ArrowLeft size={16} className="mr-1" />
          {cmsNew("cms.new.editor.backToPages", "Pages")}
        </Button>
      }
    >
      <div className="px-6 py-4">
        {statusQuery.isLoading ? (
          <p className="py-12 text-center text-sm text-slate-400">
            {cmsNew("cms.new.pages.loading", "Chargement…")}
          </p>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <p className="text-sm text-slate-500">
              {cmsNew(
                "cms.new.import.nothing",
                "Aucun contenu à importer depuis l'ancien CMS.",
              )}
            </p>
            <Button size="sm" onClick={() => navigate("/cms/advanced")}>
              {cmsNew("cms.new.editor.backToPages", "Pages")}
            </Button>
          </div>
        ) : (
          <>
            <div className="mb-3 flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">
                {cmsNew("cms.new.import.selectedCount", "Pages sélectionnées")} :{" "}
                {selectedCount} / {importable.length}
              </span>
              <button
                type="button"
                disabled={importable.length === 0}
                className="text-xs font-medium text-sky-600 hover:text-sky-700 disabled:opacity-40"
                onClick={() =>
                  setSelected(
                    selectedCount === importable.length
                      ? new Set()
                      : new Set(importable.map((it) => it.kind)),
                  )
                }
              >
                {selectedCount === importable.length
                  ? cmsNew("cms.new.import.deselectAll", "Tout désélectionner")
                  : cmsNew("cms.new.import.selectAll", "Tout sélectionner")}
              </button>
            </div>

            <ul className="flex flex-col gap-2">
              {items.map((it) => {
                const isSelected = selected.has(it.kind);
                const alreadyImported = !!it.puckPageId;
                return (
                  <li key={it.kind}>
                    <button
                      type="button"
                      disabled={alreadyImported}
                      onClick={() => toggle(it.kind)}
                      className={`flex w-full items-center gap-3 rounded-lg border p-3.5 text-left transition-colors ${
                        alreadyImported
                          ? "cursor-not-allowed border-slate-200 bg-slate-50 opacity-60"
                          : isSelected
                            ? "border-sky-200 bg-sky-50/40"
                            : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                    >
                      <span
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                          isSelected && !alreadyImported
                            ? "border-sky-500 bg-sky-500 text-white"
                            : "border-slate-300 bg-white"
                        }`}
                      >
                        {isSelected && !alreadyImported && <Check size={13} />}
                      </span>
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                        <FileText size={17} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-slate-900">
                          {it.label}
                        </span>
                        <span className="mt-1 inline-flex items-center gap-1 rounded-full border border-sky-100 bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-700">
                          <LayoutTemplate size={11} />
                          {cmsNew("cms.new.import.detectedTemplate", "Modèle détecté")} :{" "}
                          {DETECTED_TEMPLATE[it.kind] ??
                            cmsNew("cms.new.import.dedicatedPage", "Page dédiée")}
                        </span>
                      </span>
                      {alreadyImported && (
                        <Badge variant="secondary" className="text-xs">
                          {cmsNew("cms.new.import.alreadyImported", "Déjà importée")}
                        </Badge>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>

            <div className="mt-5 flex items-center justify-between border-t border-slate-200 pt-4">
              <p className="max-w-md text-xs leading-relaxed text-slate-400">
                {cmsNew(
                  "cms.new.import.footnote",
                  "Chaque page importée est enregistrée en brouillon, à vérifier avant publication.",
                )}
              </p>
              <Button
                disabled={selectedCount === 0 || importMutation.isPending}
                onClick={() => importMutation.mutate()}
              >
                {importMutation.isPending
                  ? cmsNew("cms.new.import.importing", "Import en cours…")
                  : `${cmsNew("cms.new.import.importBtn", "Importer")} ${selectedCount} ${cmsNew("cms.new.import.pagesWord", "page(s)")}`}
              </Button>
            </div>
          </>
        )}
      </div>
    </CmsAdminShell>
  );
}
